import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Building2,
  Bot,
  PhoneCall,
  Package,
  Plus,
  ArrowRight,
  Clock,
  TrendingUp,
} from 'lucide-react';
import { getServiceBusinesses, getServiceAgents, getServiceCalls } from '../../lib/api';
import { formatRelativeTime } from '../../lib/utils';

export default function ServiceDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    businesses: 0,
    agents: 0,
    calls: 0,
  });
  const [recentBusinesses, setRecentBusinesses] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [businessesRes, agentsRes, callsRes] = await Promise.all([
        getServiceBusinesses({ limit: 5 }),
        getServiceAgents({ limit: 1 }),
        getServiceCalls({ limit: 5 })
      ]);
      
      setStats({
        businesses: businessesRes.data.total,
        agents: agentsRes.data.total,
        calls: callsRes.data.total,
      });
      setRecentBusinesses(Array.isArray(businessesRes?.data?.businesses) ? businessesRes.data.businesses : []);
      setRecentCalls(Array.isArray(callsRes?.data?.calls) ? callsRes.data.calls : []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-20" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Service Dashboard</h1>
          <p className="text-slate-500">Manage businesses, agents, and subscriptions</p>
        </div>
        <div className="flex gap-3">
          <Button asChild variant="outline">
            <Link to="/service/agents">
              <Bot className="w-4 h-4 mr-2" />
              Manage Agents
            </Link>
          </Button>
          <Button asChild className="bg-amber-600 hover:bg-amber-700">
            <Link to="/service/businesses">
              <Building2 className="w-4 h-4 mr-2" />
              Manage Businesses
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100">
                <Building2 className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Businesses</p>
                <p className="text-2xl font-bold text-slate-900">{stats.businesses}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-indigo-100">
                <Bot className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active Agents</p>
                <p className="text-2xl font-bold text-slate-900">{stats.agents}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100">
                <PhoneCall className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Calls</p>
                <p className="text-2xl font-bold text-slate-900">{stats.calls}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Businesses */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-amber-600" />
              Recent Businesses
            </CardTitle>
            <Button variant="outline" size="sm" asChild>
              <Link to="/service/businesses">
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentBusinesses.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No businesses yet</p>
                <Button asChild className="mt-4 bg-amber-600 hover:bg-amber-700">
                  <Link to="/service/businesses">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Business
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentBusinesses.map((business) => (
                  <Link
                    key={business.user_id}
                    to={`/service/businesses/${business.user_id}`}
                    className="block p-4 rounded-lg border border-slate-200 hover:border-amber-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900">{business.name}</h4>
                        {business.company_name && (
                          <p className="text-sm text-slate-500">{business.company_name}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{business.email}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {formatRelativeTime(business.created_at)}
                      </Badge>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Calls */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-green-600" />
              Recent Calls
            </CardTitle>
            <Button variant="outline" size="sm" asChild>
              <Link to="/service/calls">
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentCalls.length === 0 ? (
              <div className="text-center py-8">
                <PhoneCall className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No calls recorded yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentCalls.map((call) => (
                  <Link
                    key={call.call_id}
                    to={`/service/calls/${call.call_id}`}
                    className="block p-4 rounded-lg border border-slate-200 hover:border-green-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900">
                          {call.customer?.name || 'Unknown Customer'}
                        </h4>
                        <p className="text-sm text-slate-500 line-clamp-1">
                          {call.summary || 'No summary available'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">
                            {call.duration_minutes || 0} min
                          </span>
                          {call.agent?.name && (
                            <Badge variant="outline" className="text-xs">
                              {call.agent.name}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-slate-400">
                        {formatRelativeTime(call.started_at)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button variant="outline" asChild className="h-auto py-6 flex-col">
              <Link to="/service/businesses">
                <Building2 className="w-6 h-6 mb-2 text-amber-600" />
                <span>Add Business</span>
              </Link>
            </Button>
            <Button variant="outline" asChild className="h-auto py-6 flex-col">
              <Link to="/service/agents">
                <Bot className="w-6 h-6 mb-2 text-indigo-600" />
                <span>Create Agent</span>
              </Link>
            </Button>
            <Button variant="outline" asChild className="h-auto py-6 flex-col">
              <Link to="/service/businesses">
                <Package className="w-6 h-6 mb-2 text-green-600" />
                <span>Add Package</span>
              </Link>
            </Button>
            <Button variant="outline" asChild className="h-auto py-6 flex-col">
              <Link to="/service/calls">
                <PhoneCall className="w-6 h-6 mb-2 text-purple-600" />
                <span>View Calls</span>
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
